import { EProtocolNoteStatus } from "../enums/eprotocol-note-status";
import { EMPTY_PROTOCOL_NOTE_ANCHOR, ProtocolNoteAnchor } from "./protocol-note-anchor";

/**
 * Le casing de l'API des notes est asymétrique : les réponses sont en
 * snake_case (JMS) alors que les corps de requête sont en camelCase (serializer
 * Symfony). `ProtocolNote` et `ProtocolNoteRequest` ne sont donc pas
 * interchangeables, même quand ils portent les mêmes informations.
 *
 * Les champs nuls sont par ailleurs absents des réponses : tout ce qui est
 * optionnel ci-dessous peut ne pas exister dans la charge utile.
 */

export interface ProtocolNoteUser {
	id: number;
	firstname: string;
	lastname: string;
}

export interface ProtocolNoteProtocol {
	id: number;
	label: string;
	codification?: string;
}

export interface ProtocolNoteProtocolUpdate {
	id: number;
	major_version: number;
	minor_version: number;
	updated: string;
	protocol: ProtocolNoteProtocol;
}

export interface ProtocolNote {
	id: number;
	content: string;
	selected_text?: string;
	selection_anchor: ProtocolNoteAnchor;
	status: EProtocolNoteStatus;
	shared_to_manager: boolean;
	shared_at?: string;
	created_at: string;
	updated_at: string;
	/** Déjà formatée (« 2.1 ») : à afficher telle quelle. */
	version: string | null;
	/** Le protocole a été republié depuis : l'ancre ne vaut plus pour la version courante. */
	outdated: boolean;
	user: ProtocolNoteUser;
	protocol_update: ProtocolNoteProtocolUpdate;
}

/** Corps de requête du POST et du PUT. camelCase, contrairement aux réponses. */
export interface ProtocolNoteRequest {
	content: string;
	selectedText: string | null;
	selectionAnchor: ProtocolNoteAnchor;
}

export interface ProtocolNotesPage {
	items: ProtocolNote[];
	total: number;
	maxPerPage: number;
	currentPage: number;
	nbPages: number;
}

export const PROTOCOL_NOTE_CONTENT_MAX_LENGTH = 5000;
export const PROTOCOL_NOTE_SELECTED_TEXT_MAX_LENGTH = 10000;

/**
 * Le PUT est un remplacement complet : omettre `selectedText` ou
 * `selectionAnchor` efface ce qui était stocké. On repart donc toujours de la
 * note existante plutôt que de n'envoyer que le champ modifié.
 */
export function buildProtocolNoteRequest(note: ProtocolNote, content: string): ProtocolNoteRequest {
	return {
		content: content.trim(),
		selectedText: note.selected_text ?? null,
		selectionAnchor: note.selection_anchor ?? EMPTY_PROTOCOL_NOTE_ANCHOR,
	};
}

export function isProtocolNoteContentValid(content: string): boolean {
	const trimmed = content?.trim() ?? "";
	return trimmed.length > 0 && trimmed.length <= PROTOCOL_NOTE_CONTENT_MAX_LENGTH;
}
